import {
  formatDate,
  formatOrgnr,
  formatTime,
  parseDashboardDate,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardFormatting";
import {type ReportOverviewProps} from "@/ReactInit/Scripts/DashboardPage/types";
import {DashboardAlertBox} from "@/ReactInit/Scripts/DashboardPage/components/DashboardAlertBox";
import {DashboardButton} from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import {cn, isReportSentToFabric} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";
import {useIsMobile} from "@/ReactInit/Scripts/DashboardPage/hooks/useBreakpoint";

const AdminDashboardReportPage = (
  {
    reports,
    producers,
    selectedReportIds,
    onToggleReport,
    onToggleAllReports,
    onSelectReport,
    visibleCount,
    onChangeVisibleCount,
    reportFilters
  }: ReportOverviewProps) => {
  if (reports.length === 0) {
    console.log("Filters:", reportFilters)
    const isUnfiltered = reportFilters && reportFilters.includes("ALL");
    const isDraftFilter = reportFilters && reportFilters.includes("DRAFTS_ONLY");
    return (
      <DashboardAlertBox
        type="info"
        label={isUnfiltered ? "Ingen rapporter." : isDraftFilter ? "Ingen foreløpige rapporter." : "Filteret ga ingen resultater."}
      />
    );
  }

  const isMobile = useIsMobile();

  const findProducer = (producerId: string) =>
    producers.find((p) => p.id === producerId);

  const totalReports = reports.length;
  const effectiveVisibleCount = Math.min(visibleCount, totalReports);
  const visibleReports = reports.slice(0, effectiveVisibleCount);

  const allIds = reports.map((r) => r.id);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selectedReportIds.includes(id));

  const isDraftOnlyView = reports.every((r) => !parseDashboardDate(r.reportDate));

  const notPublishedReports = isDraftOnlyView
    ? []
    : reports.filter((r) => !isReportSentToFabric(r));

  const canShowMore = effectiveVisibleCount < totalReports;

  const noun = isDraftOnlyView
    ? totalReports === 1
      ? "utkast"
      : "utkast"
    : totalReports === 1
      ? "rapport"
      : "rapporter";

  return (
    <div className="flex flex-col gap-5 tablet:gap-10">
      <div>
        <p className={cn(isMobile && "min-h-12")}>
          {selectedReportIds.length > 0 ? (
            <>
              {selectedReportIds.length}{" "}
              {isDraftOnlyView
                ? selectedReportIds.length === 1
                  ? "utkast"
                  : "utkast"
                : selectedReportIds.length === 1
                  ? "rapport"
                  : "rapporter"}{" "}
              valgt av totalt {totalReports} {noun}
            </>
          ) : (
            <>
              Viser {effectiveVisibleCount} av {totalReports} {noun}.
              {!isDraftOnlyView && notPublishedReports.length > 0 && (
                <>
                  {" "}
                  Det er {notPublishedReports.length}{" "}
                  {notPublishedReports.length === 1 ? "rapport" : "rapporter"}{" "}
                  som ikke er sendt til Fabric.
                </>
              )}
              {isDraftOnlyView && (
                <> Utkast kan ikke sendes til Fabric.</>
              )}
            </>
          )}
        </p>
      </div>

      <div className="min-w-0 overflow-x-auto pb-2.5">
        <table className="dashboardTable w-max min-w-max whitespace-nowrap">
          <thead>
          <tr>
            <th
              className="dashboardTable-checkbox cursor-pointer"
              onClick={() => onToggleAllReports(allIds)}
            >
              <div className="dropdown-checkbox">
                <div
                  className={`dropdown-checkbox-box ${
                    allSelected ? "dropdown-checkbox-checked" : ""
                  }`}
                ></div>
              </div>
            </th>

            <th>Virksomhet</th>
            <th>Rapportår</th>
            <th>Organisasjonsnummer</th>
            <th>Rapportert dag</th>
            <th>Tid</th>
            <th>Tilstand</th>
            {!isDraftOnlyView && <th>Sendt til Fabric</th>}
          </tr>
          </thead>

          <tbody>
          {visibleReports.map((r) => {
            const producer = findProducer(r.producerId);
            const virksomhet = producer?.name ?? "Ukjent virksomhet";
            const orgnr = producer?.orgnr ?? "–";

            const reportDt = parseDashboardDate(r.reportDate);
            const changedDt = parseDashboardDate(r.changedDate);

            const isDraft = !reportDt;

            const isEdited =
              !!changedDt &&
              !!reportDt &&
              changedDt.getTime() > reportDt.getTime();

            const sentToFabric = isReportSentToFabric(r);

            const effectiveDt = changedDt ?? reportDt ?? null;
            const isSelected = selectedReportIds.includes(r.id);

            const handleRowActivate = () => {
              onSelectReport(r.id);
            };

            const rowHighlight =
              !isDraftOnlyView && !sentToFabric ? "highlight" : "";

            const ariaText = isDraft
              ? `Rediger utkast for ${virksomhet}`
              : `Rediger rapport for ${virksomhet}, ${formatDate(effectiveDt) || "dato ukjent"}`;

            return (
              <tr
                key={r.id}
                className={`${rowHighlight} cursor-pointer`}
                tabIndex={0}
                role="button"
                aria-label={ariaText}
                onClick={handleRowActivate}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleRowActivate();
                  }
                }}
              >
                <td
                  className="dashboardTable-checkbox cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleReport(r.id);
                  }}
                >
                  <div className="dropdown-checkbox">
                    <div
                      className={`dropdown-checkbox-box ${
                        isSelected ? "dropdown-checkbox-checked" : ""
                      }`}
                    ></div>
                  </div>
                </td>

                <td>{virksomhet}</td>
                <td>{r.year}</td>
                <td>{formatOrgnr(orgnr)}</td>
                <td>{reportDt ? formatDate(reportDt) : "–"}</td>
                <td>{reportDt ? formatTime(reportDt) : "–"}</td>
                <td>{isDraft ? "Utkast" : isEdited ? "Redigert" : ""}</td>
                {!isDraftOnlyView && <td>{sentToFabric ? "Ja" : "Nei"}</td>}
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>
      {canShowMore && (
        <div className="flex justify-center">
          <DashboardButton
            label="Vis flere"
            variant="darkplain"
            rounded="full"
            onClick={() => onChangeVisibleCount(effectiveVisibleCount + 10)}
          />
        </div>
      )}
    </div>
  );
};

export default AdminDashboardReportPage;
